import { PerformanceEmployeePerformanceInsightsSummaryFilter } from 'generated/resources/datatable.graphql'
import { HeadcountInsightsDocument } from 'generated/resources/graphql'
import { omit } from 'lodash'

import useFilters from 'modules/core/components/DataTable/hooks/useFilters'
import { useQuery } from 'modules/core/hooks/useQuery'

import { GroupByType } from 'modules/performance_analytics/components/helpers'

import { useProcessChartData } from '../lib/chartDataProcessor'
import { getFilterPathForGroupBy } from '../lib/chartUtils'
import { extractGroupByData } from '../lib/dataExtractor'
import { HeadcountData } from '../lib/types'

interface UseHeadcountDataProps {
  groupBy: GroupByType | null | undefined
}

export const useHeadcountData = ({ groupBy }: UseHeadcountDataProps): HeadcountData | null => {
  const {
    getFiltersVariables,
    filters: enabledFilters,
    availableFilters,
    onAddFilter,
    onUpdateFilter,
  } = useFilters<PerformanceEmployeePerformanceInsightsSummaryFilter>()

  const baseFilters = JSON.parse(JSON.stringify(getFiltersVariables().filter))

  // Remove the corresponding filter from dataTableFilters if groupBy is present
  const dataTableFilters = groupBy
    ? omit(baseFilters, getFilterPathForGroupBy(groupBy))
    : baseFilters

  const { data, loading } = useQuery(HeadcountInsightsDocument, {
    variables: {
      filter: dataTableFilters,
      groupByGender: groupBy === 'gender',
      groupByLegalEntity: groupBy === 'legal_entity',
      groupByManager: groupBy === 'manager',
      groupByNationality: groupBy === 'nationality',
      groupByTeam: groupBy === 'team',
      groupByWorkspace: groupBy === 'workspace',
      groupBy: groupBy ?? '',
      totalCount: !groupBy,
    },
  })

  const extractedData = extractGroupByData(data, groupBy)

  const { chartData, legalEntities, teams, locations, employees } = useProcessChartData(
    extractedData,
    groupBy
  )

  return {
    chartData,
    hasRecords: !!data?.performance.total?.totalCount,
    totalCount: extractedData.totalCount,
    dataTableFilters: baseFilters,
    enabledFilters,
    availableFilters,
    onAddFilter,
    onUpdateFilter,
    legalEntities,
    teams,
    locations,
    employees,
    loading,
  }
}
